import React, { useState, useContext, useMemo } from 'react';

import { Rate, List } from 'antd';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';
import { showLog } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';

import { useRating, useUpdateRating } from '@hooks/API/UseRating';
import useAreaAccentColor from '@hooks/Layout/UseAreaAccentColor';

import FlexContainer from '@components/Gui/FlexContainer';
import MiniLoading from '@components/Gui/MiniLoading';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDividerVertical from '@components/Gui/NwDividerVertical';
import NwDrawer from '@components/Gui/NwDrawer';
import { StyledRateContainer } from '@components/Gui/NwForm/Rating';
import UserIcon from '@components/UserIcon/UserIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const UserRating = styled(StyledRateContainer)`
    margin-left: 0.5rem;
    margin-bottom: 0.25rem;
`;

const AverageRatingContainer = styled(FlexContainer)`
    font-size: 0.95rem;
`;

const AverageRating = styled(StyledRateContainer)`
    margin: 0 0.5rem;
`;

const MediaRatings = ({ objectId, objectType, onMediaUpdate, vertical }) => {
    const ratingColor = useAreaAccentColor();
    const { data: rating } = useRating(objectId, objectType);
    const { mutateAsync: onUpdateRating } = useUpdateRating();
    const [loading, setLoading] = useState(false);
    const [showRatings, setShowRatings] = useState(false);
    const { cachedList, cachedListLoaded } = useContext(ListContext);

    const getObjectIdNameByType = (type) => {
        switch (type) {
            case 'Model':
                return 'ContactID';
            case 'Customer':
                return 'ContactID';
            case 'Document':
                return 'DocumentID';
            case 'Playlist':
                return 'PlaylistID';
            default:
                return null;
        }
    };

    const handleUserRate = async (rate) => {
        const data = {
            [getObjectIdNameByType(objectType)]: objectId,
            Rating: rate,
        };

        setLoading(true);
        try {
            await onUpdateRating({ data: data, type: objectType, ID: objectId });
            if (onMediaUpdate) {
                onMediaUpdate();
            }
            setLoading(false);
        } catch (error) {
            console.error('User Rate Error => ', error);
        }
    };

    const formatRating = (rating) => {
        return !(rating % 1) ? rating : rating.toFixed(1);
    };

    const handleViewRatings = () => {
        if (rating.ratings.length) {
            setShowRatings(true);
        }
    };

    const handleCloseRatings = () => {
        setShowRatings(false);
    };

    const getUserInfoByUserId = (userId) => {
        let user = null;
        let userName = '';
        let userCoverGuid = '';
        let letters = '';
        if (cachedListLoaded) {
            user = cachedList.users.items.find((usr) => usr.ID === userId);
            if (user) {
                userName = user.Name + ' ' + user.Surname;
                userCoverGuid = user.CoverGuid;
                letters = user.Name.toUpperCase()[0] + ' ' + user.Surname.toUpperCase()[0];
            } else {
                userName = 'Utente Farlocco';
                letters = 'UF';
            }
        }

        return { userName, userCoverGuid, letters, userId };
    };

    const ratingHistories = useMemo(() => {
        let ratingHistories = [];
        if (rating && rating.ratings) {
            ratingHistories = rating.ratings.map((rfi) => ({
                ...rfi,
                key: rfi.ID,
                createdBy: getUserInfoByUserId(rfi.UserCreated),
                dateCreated: showLog(rfi.DateModified || rfi.DateCreated),
            }));
        }
        return ratingHistories;

    }, [rating]);

    let averageRating =
        rating && rating.ratings.length
            ? rating.ratings.map((userRate) => userRate.Rating).reduce((rat1, rat2) => rat1 + rat2, 0) /
              rating.ratings.length
            : 0;

    return (
        <NwCard title="Ratings" icon={byPrefixAndName.fasl['star']} horizontal={!vertical}>
            {rating && (
                <FlexContainer mg="2px 0 0">
                    <div>Your Rating</div>
                    <UserRating ratingColor={ratingColor}>
                        <Rate
                            style={{ fontSize: '16px' }}
                            disabled={loading}
                            value={rating.currentUserRating}
                            onChange={handleUserRate}
                        />
                        &nbsp;
                        {loading && <MiniLoading />}
                    </UserRating>
                    <NwDividerVertical />
                    <AverageRatingContainer>
                        <div>Average Rating</div>
                        <AverageRating ratingColor={ratingColor}>
                            <Rate disabled style={{ fontSize: '14px' }} value={rating.averageRating} />
                        </AverageRating>
                        {rating.ratings.length > 0 && (
                            <FlexContainer>
                                <div>{formatRating(averageRating)}</div>&nbsp; (
                                <div
                                    className={rating.ratings.length && 'nw-link'}
                                    onClick={handleViewRatings}
                                >{`${rating.ratings.length} users`}</div>
                                )
                            </FlexContainer>
                        )}
                    </AverageRatingContainer>
                </FlexContainer>
            )}
            {showRatings && (
                <NwDrawer title="Ratings" onClose={handleCloseRatings}>
                    <List
                        itemLayout="horizontal"
                        dataSource={ratingHistories}
                        renderItem={(rating) => {
                            const { createdBy, dateCreated, Rating } = rating;
                            return (
                                <List.Item className="ant-list-item-top">
                                    <FlexContainer justifyContent="space-between" fullWidth>
                                        <FlexContainer>
                                            <UserIcon
                                                userId={createdBy.userId}
                                                coverGuid={createdBy.userCoverGuid}
                                                letters={createdBy.letters}
                                                size="medium"
                                            />
                                            <FlexContainer column alignItems="flex-start" mg="0 16px">
                                                <div>{createdBy.userName}</div>
                                                <div>{dateCreated}</div>
                                            </FlexContainer>
                                        </FlexContainer>
                                        <StyledRateContainer ratingColor={ratingColor}>
                                            <Rate disabled style={{ fontSize: '16px' }} value={Rating} />
                                        </StyledRateContainer>
                                    </FlexContainer>
                                </List.Item>
                            );
                        }}
                    />
                </NwDrawer>
            )}
        </NwCard>
    );
};

export default MediaRatings;
